import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { Collateral, CollateralStatus, Currency, EntityType, Index, Market, MarketType, ProductSector, RepoType } from 'api'
import { Locale, Translation } from 'locale'
import { CollateralHelper, ProductSectorHelper } from 'model'
import { Field } from 'view/components/inputs'
import { Combobox, ComboboxProps, Selectbox, SelectboxProps } from 'view/components/options'
import { createEntitySelectbox } from './EntityOptions'
import { createSubstitutableCollateralIdListSelector } from 'selectors/collateralSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { enabledIndexIdsSelector } from 'selectors/indexSelectors'
import { createSortedProductSectorIdsSelector } from 'selectors/productSelectors'

/* Substitutable Collateral */

interface SubstitutableCollateralComboboxProps extends ComboboxProps<Collateral, string> {
  fundId: string
  counterpartyId: string
  productId: string | null
  currency: Currency
  parentDealBasketId?: string | null
  originalIsin: string
  repoType: RepoType
  marketType: MarketType | null
}

const substitutableCollateralFundIdSelector = (_: {}, props: SubstitutableCollateralComboboxProps) => props.fundId
const substitutableCollateralCounterpartyIdSelector = (_: {}, props: SubstitutableCollateralComboboxProps) => props.counterpartyId
const substitutableCollateralParentDealBasketIdSelector = (_: {}, props: SubstitutableCollateralComboboxProps) => props.parentDealBasketId
const substitutableCollateralOriginalIsinSelector = (_: {}, props: SubstitutableCollateralComboboxProps) => props.originalIsin
const substitutableCollateralOriginalRepoTypeSelector = (_: {}, props: SubstitutableCollateralComboboxProps) => props.repoType
const substitutableCollateralOriginalProductIdSelector = (_: {}, props: SubstitutableCollateralComboboxProps) => props.productId
const substitutableCollateralOriginalCurrencySelector = (_: {}, props: SubstitutableCollateralComboboxProps) => props.currency
const substitutableCollateralMarketTypeSelector = (_: {}, props: SubstitutableCollateralComboboxProps) => props.marketType

const substitutableCollateralMapStateToProps = createStructuredSelector({
  optionKeys: createSubstitutableCollateralIdListSelector(
    substitutableCollateralFundIdSelector,
    substitutableCollateralCounterpartyIdSelector,
    substitutableCollateralParentDealBasketIdSelector,
    substitutableCollateralOriginalIsinSelector,
    substitutableCollateralOriginalRepoTypeSelector,
    substitutableCollateralOriginalProductIdSelector,
    substitutableCollateralOriginalCurrencySelector,
    substitutableCollateralMarketTypeSelector
  ),
  getOption: createEntityGetterSelector(EntityType.COLLATERAL)
})

export const SubstitutableCollateralCombobox = connect(substitutableCollateralMapStateToProps, null, null, { forwardRef: true })(
  Combobox
) as React.ComponentType<Omit<SubstitutableCollateralComboboxProps, 'optionKeys' | 'getOption'>>

SubstitutableCollateralCombobox.defaultProps = {
  getOptionText: CollateralHelper.getName,
  getOptionDisabled: collateral => collateral.status !== CollateralStatus.ENABLED,
  filterFields: ['isin'],
  noItemsText: Locale.translate(Translation.SC_N_COLLATERALS, { count: 0 })
}

export const SubstitutableCollateralComboboxField = Field(SubstitutableCollateralCombobox)

/* Product Sector */

export interface ProductSectorSelectboxProps extends SelectboxProps<ProductSector, string> {
  market?: Market | null
  bothMarkets?: boolean
}

const marketSelector = (_: {}, props: ProductSectorSelectboxProps) => props.market
const bothMarkets = (_: {}, props: ProductSectorSelectboxProps) => props.bothMarkets

const productSectorMapStateToProps = createStructuredSelector({
  optionKeys: createSortedProductSectorIdsSelector(marketSelector, bothMarkets),
  getOption: createEntityGetterSelector(EntityType.PRODUCT_SECTOR)
})

export const ProductSectorSelectbox = connect(productSectorMapStateToProps, null, null, { forwardRef: true })(
  Selectbox
) as React.ComponentType<Omit<ProductSectorSelectboxProps, 'optionKeys' | 'getOption'>>

ProductSectorSelectbox.defaultProps = {
  getOptionText: ProductSectorHelper.getName,
  noItemsText: Locale.translate(Translation.SC_N_PRODUCT_SECTORS, { count: 0 })
}

export const ProductSectorSelectboxField = Field(ProductSectorSelectbox)

/* Index */

const indexMapStateToProps = createStructuredSelector({
  optionKeys: enabledIndexIdsSelector
})

const IndexEntitySelectbox = createEntitySelectbox(EntityType.INDEX)
const IndexSelectboxComponent: React.FunctionComponent<SelectboxProps<Index, string>> = props => {
  return <IndexEntitySelectbox {...props} />
}
const IndexSelectbox = connect(indexMapStateToProps, null, null, { forwardRef: true })(IndexSelectboxComponent)

export const IndexSelectboxField = Field(IndexSelectbox)
